/**
 * Converts Float32 PCM to the little-endian wire representation required by
 * the audio proxy. DataView is deliberately used instead of a typed-array
 * view, whose byte order follows the platform.
 */
export function float32ToLittleEndianBytes(samples: Float32Array): Uint8Array {
  const bytes = new Uint8Array(samples.length * Float32Array.BYTES_PER_ELEMENT);
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);

  for (let index = 0; index < samples.length; index += 1) {
    view.setFloat32(index * Float32Array.BYTES_PER_ELEMENT, samples[index], true);
  }

  return bytes;
}

export function littleEndianBytesToFloat32(bytes: Uint8Array): Float32Array {
  if (bytes.byteLength % Float32Array.BYTES_PER_ELEMENT !== 0) {
    throw new RangeError("Float32 PCM byte length must be divisible by four.");
  }

  const samples = new Float32Array(
    bytes.byteLength / Float32Array.BYTES_PER_ELEMENT,
  );
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);

  for (let index = 0; index < samples.length; index += 1) {
    samples[index] = view.getFloat32(
      index * Float32Array.BYTES_PER_ELEMENT,
      true,
    );
  }

  return samples;
}

function bytesToBinaryString(bytes: Uint8Array): string {
  // Keeping chunks below argument-spread limits also makes this safe if a
  // transport later batches more than the normal 200 ms PCM chunk.
  const chunkSize = 0x8000;
  let result = "";

  for (let start = 0; start < bytes.length; start += chunkSize) {
    const end = Math.min(start + chunkSize, bytes.length);
    let chunk = "";
    for (let index = start; index < end; index += 1) {
      chunk += String.fromCharCode(bytes[index]);
    }
    result += chunk;
  }

  return result;
}

export function bytesToBase64(bytes: Uint8Array): string {
  return btoa(bytesToBinaryString(bytes));
}

export function base64ToBytes(encoded: string): Uint8Array {
  const binary = atob(encoded);
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return bytes;
}

/** Serialize Float32 PCM as explicitly little-endian bytes and then base64. */
export function float32ToBase64(samples: Float32Array): string {
  return bytesToBase64(float32ToLittleEndianBytes(samples));
}

export function base64ToFloat32(encoded: string): Float32Array {
  return littleEndianBytesToFloat32(base64ToBytes(encoded));
}
